import React, { useEffect, useState } from 'react';
import Slider from 'react-slick';

import Loader from '../Loader/Loader';
import type { VideoCourse, VideoCourseResponse } from '@/types/video';
import ArrowRightIcon from '../Icons/Action/ArrowRightIcon';
import { useSelectedCategories } from '@/redux/selectors/categorySelector';
import { getVideoCourses } from '@/services/strapi/strapiCategoryService';
import VideoCourseCard from '../Video/VideoCourseCard';
import Link from 'next/link';
import { ROUTES } from '@/config/routes';
import { useSelectedLanguage } from '@/redux/selectors/languageSelector';
import { T } from '@tolgee/react';

const NextArrow = ({ onClick, isVisible }: { onClick?: () => void; isVisible: boolean }) => (
  <div
    className={`absolute right-6 z-10 top-0 bottom-0 m-auto cursor-pointer rounded-[100px] border border-white bg-white/30 backdrop-blur-[10px] w-[60px] h-[60px] flex items-center justify-center transition-opacity duration-300 ${
      isVisible ? 'opacity-100' : 'opacity-0 pointer-events-none'
    }`}
    onClick={onClick}
  >
    <ArrowRightIcon width={20} height={20} />
  </div>
);

const PrevArrow = ({ onClick, currentSlide }: { onClick?: () => void; currentSlide: number }) =>
  currentSlide > 0 ? (
    <div
      className="absolute left-6 z-10 top-0 bottom-0 m-auto cursor-pointer rounded-[100px] border border-white bg-white/30 backdrop-blur-[10px] w-[60px] h-[60px] flex items-center justify-center"
      onClick={onClick}
    >
      <ArrowRightIcon width={20} height={20} className="transform rotate-180" />
    </div>
  ) : null;

const VideoCourseSlider: React.FC = () => {
  const [videoCourses, setVideoCourses] = useState<VideoCourse[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);
  const selectedCategories = useSelectedCategories();
  const locale = useSelectedLanguage();

  useEffect(() => {
    const fetchVideoCourses = async () => {
      setLoading(true);
      try {
        const categorySlugs = selectedCategories.filter((category) => category !== 'all');
        const response = await getVideoCourses(5, categorySlugs, 0, locale);

        const mappedCourses: VideoCourse[] = response.data.map((course: VideoCourseResponse) => ({
          imageAlt: course.attributes.title,
          imageSrc: course.attributes.thumbnail?.data.attributes.url || '',
          title: course.attributes.title,
          duration: '',
          author: course.attributes.expert.data?.attributes.name || 'Unknown Author',
          authorImage: course.attributes.expert.data?.attributes.picture?.data,
          id: course.id,
          videos: course.attributes.videos,
          languages:
            course.attributes.languages?.data.map((lang) => lang.attributes.name).join(', ') ||
            'N/A',
        }));

        setVideoCourses(mappedCourses);
      } catch (error) {
        console.error('Error fetching video courses:', error);
        setError(error as Error);
      } finally {
        setLoading(false);
      }
    };

    void fetchVideoCourses();
  }, [selectedCategories, locale]);

  const [currentSlide, setCurrentSlide] = useState(0);
  const slideCount = videoCourses.length;
  const settings = {
    dots: false,
    infinite: false,
    speed: 500,
    slidesToShow: 2.06,
    slidesToScroll: 1,
    centerMode: false,
    nextArrow: <NextArrow isVisible={currentSlide < slideCount - 2} />,
    prevArrow: <PrevArrow currentSlide={currentSlide} />,
    beforeChange: (current: number, next: number) => setCurrentSlide(Math.round(next)),
    responsive: [
      {
        breakpoint: 1024,
        settings: {
          slidesToShow: 2.06,
          slidesToScroll: 1,
        },
      },
      {
        breakpoint: 768,
        settings: {
          slidesToShow: 1.2,
          slidesToScroll: 1,
        },
      },
    ],
  };

  if (loading) return <Loader position={'absolute'} />;
  if (error) return <div>Error loading video courses</div>;
  if (!videoCourses.length) return null;
  return (
    <div className="relative p-5 md:p-12 pr-0 mb-3 w-full">
      <div className="relative">
        <h2 className="text-4xl">
          <T>home.filtered.videocourses_section.title.label</T>
        </h2>
      </div>
      <Slider {...settings} className="mt-4">
        {videoCourses.map((course, index) => (
          <div key={index}>
            <Link href={ROUTES.VIDEO_COURSE(course.id, course.title)} passHref prefetch={true}>
              <VideoCourseCard course={course} />
            </Link>
          </div>
        ))}
      </Slider>
    </div>
  );
};

export default VideoCourseSlider;
